import React, { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import * as yup from "yup";
import { toast } from "react-toastify";

import {
  Heading,
  Input,
  Form,
  Group,
  Checkbox,
  LoadingIcon,
  File,
} from "@peracto/peracto-ui";
import Select from "react-select";
import { GET_LIST, UPDATE, useClient } from "@peracto/client";
import BrandRestrictor from "./components/brandRestrictor";
import BrandSorter from "./components/brandSorter";

const formatDataForForm = (data, categoryOptions) => {
  const formData = [];

  for (const [key, value] of Object.entries(data)) {
    let formattedValue = value?.value;
    const isBrandConfig = key.includes("categoryBrand");
    if (key === "menuItemsExcluded") {
      formattedValue = formatExcludedItems(value?.value, categoryOptions);
    }
    formData.push({
      field: key,
      dataType: !isBrandConfig ? value?.dataType : null,
      value: !isBrandConfig ? formattedValue : value,
    });
  }

  return formData;
};

const formatDataForAPI = (data, categoryOptions) => {
  const babbageAccountAttributes = [];

  for (const [key, val] of Object.entries(data)) {
    if (key !== "mainAccountNumber") {
      if (
        key !== "categoryBrandPriorities" &&
        key !== "categoryBrandRestrictions"
      ) {
        let formattedVal = val;
        if (typeof formattedVal === "boolean") {
          formattedVal = `${formattedVal}`;
        }
        if (key === "excludeMenuItems") {
          formattedVal = formatExcludedItems(formattedVal, categoryOptions);
        }
        babbageAccountAttributes.push({
          type: key,
          value: formattedVal,
        });
      }
    }
  }

  const bulkAttributes = {
    babbageAccountAttributes: babbageAccountAttributes,
    babbageAccountCategoryBrandPriorities: data?.categoryBrandPriorities,
    babbageAccountCategoryBrandRestrictions: data?.categoryBrandRestrictions,
  };

  return bulkAttributes;
};

const formatExcludedItems = (excludedItems, categoryOptions) => {
  if (!Array.isArray(excludedItems) || excludedItems.length === 0) return [];

  if (typeof excludedItems[0] === "string") {
    return excludedItems.map((item) => {
      const option = categoryOptions.find((opt) => opt.value === item);
      return option
        ? { label: option.label, value: option.value }
        : { label: item, value: item };
    });
  }

  if (typeof excludedItems[0] === "object" && excludedItems[0] !== null) {
    return excludedItems.map((item) => item.value);
  }

  return [];
};

const schema = yup.object().shape({
  mainAccountNumber: yup.string().required(),
});

export const AccountsList = () => {
  const { client } = useClient();

  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [babbageAccounts, setBabbageAccounts] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [t3CategoryOptions, setT3CategoryOptions] = useState([]);
  const [t3CategoriesLoading, setT3CategoriesLoading] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [mainAccountNumber, setMainAccountNumber] = useState("");

  const onUpdateAccount = async (formData, actions) => {
    const formattedFormData = formatDataForAPI(formData, categoryOptions);

    try {
      await client(UPDATE, "babbage-account-configuration-settings", {
        id: `/babbage-account-configuration-settings/${mainAccountNumber}`,
        data: formattedFormData,
      });

      const { data } = await client(GET_LIST, "babbage-accounts", {
        id: "babbage-accounts",
        filter: {
          mainAccountNumber,
          itemsPerPage: 10000,
        },
      });

      setNoResults(data.length === 0);
      const formattedData = formatDataForForm(data[0], categoryOptions);

      setBabbageAccounts(formattedData);

      toast.success("Babbage account settings successfully updated");
    } catch (e) {
      console.error(e);

      toast.error(
        e?.error?.body?.hasOwnProperty("hydra:description")
          ? e.error.body["hydra:description"]
          : "There was an error updating this value."
      );
    } finally {
      actions.setSubmitting(false);
    }
  };

  const getCategoryOptions = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await client(GET_LIST, "categories", {
        id: "categories",
        filter: {
          itemsPerPage: 10000,
        },
      });

      const options = data
        .map((item) => {
          return {
            label: item.heading,
            value: item.identifier,
          };
        })
        .filter((item) => item.value !== "car-parts");

      setCategoryOptions(options);
    } catch (e) {
      console.error(e);

      toast.error(
        e?.error?.body?.hasOwnProperty("hydra:description")
          ? e.error.body["hydra:description"]
          : "There was an error submitting this form."
      );
    } finally {
      setCategoriesLoading(false);
    }
  };

  const onSubmitSearchForm = async (formData, actions) => {
    setLoading(true);
    setNoResults(false);
    setMainAccountNumber(formData.mainAccountNumber);
    setBabbageAccounts([]);

    try {
      const { data } = await client(GET_LIST, "babbage-accounts", {
        id: "babbage-accounts",
        filter: {
          mainAccountNumber: formData.mainAccountNumber,
          itemsPerPage: 10000,
        },
      });

      setNoResults(data.length === 0);

      const formattedData = formatDataForForm(data[0], categoryOptions);

      setBabbageAccounts(formattedData);
    } catch (e) {
      console.error(e);

      toast.error(
        e?.error?.body?.hasOwnProperty("hydra:description")
          ? e.error.body["hydra:description"]
          : "There was an error submitting this form."
      );
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  useEffect(() => {
    getCategoryOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isKeyAccount, setIsKeyAccount] = useState(
    babbageAccounts?.find((val) => val.field === "isKeyAccount")?.value || false
  );
  const [showStrippedFooter, setShowStrippedFooter] = useState(
    babbageAccounts?.find((val) => val.field === "customBabbageAccountTheme")
      ?.value?.showStrippedFooter === "true"
      ? true
      : false
  );
  const [restrictHaynesProAccess, setRestrictHaynesProAccess] = useState(
    babbageAccounts?.find((val) => val.field === "hasHaynesProRevoked")
      ?.value || false
  );
  const [hideOmnihub, setHideOmnihub] = useState(
    babbageAccounts?.find((val) => val.field === "hideOmnihub")?.value || false
  );
  const [excludeMenuItems, setExcludeMenuItems] = useState(
    babbageAccounts?.find((val) => val.field === "menuItemsExcluded")?.value ||
      []
  );
  const [themeColours, setThemeColours] = useState({
    primaryColour:
      babbageAccounts?.find((val) => val.field === "customBabbageAccountTheme")
        ?.value?.primaryColour || "#0033A0",
    secondaryColour:
      babbageAccounts?.find((val) => val.field === "customBabbageAccountTheme")
        ?.value?.secondaryColour || "#FDDA24",
    tertiaryColour:
      babbageAccounts?.find((val) => val.field === "customBabbageAccountTheme")
        ?.value?.tertiaryColour || "#001E60",
  });

  //these states store the overall restrictions and priorities for all categories
  const [categoryBrandRestrictions, setCategoryBrandRestrictions] = useState(
    (
      babbageAccounts.find((val) => val.field === "categoryBrandRestrictions")
        ?.value || []
    ).map((restriction) => {
      return {
        category: restriction?.category?.["@id"],
        brand: restriction?.brand?.["@id"],
      };
    })
  );

  const [categoryBrandPriorities, setCategoryBrandPriorities] = useState(
    (
      babbageAccounts.find((val) => val.field === "categoryBrandPriorities")
        ?.value || []
    ).map((priority) => {
      return {
        category: priority?.category?.["@id"],
        brand: priority?.brand?.["@id"],
        priority: priority?.priority,
      };
    })
  );

  const [selectedCategory, setSelectedCategory] = useState(null);
  //these states store the restricted brands and ranked brands for the selected category
  const [restrictedBrands, setRestrictedBrands] = useState([]);
  const [prioritisedBrands, setPrioritisedBrands] = useState([]);

  useEffect(() => {
    if (babbageAccounts) {
      setIsKeyAccount(
        babbageAccounts.find((val) => val.field === "isKeyAccount")?.value ||
          false
      );

      setShowStrippedFooter(
        babbageAccounts.find((val) => val.field === "customBabbageAccountTheme")
          ?.value?.showStrippedFooter === "true"
          ? true
          : false
      );
      setRestrictHaynesProAccess(
        babbageAccounts.find((val) => val.field === "hasHaynesProRevoked")
          ?.value || false
      );
      setHideOmnihub(
        babbageAccounts.find((val) => val.field === "hideOmnihub")?.value ||
          false
      );
      setExcludeMenuItems(
        babbageAccounts.find((val) => val.field === "menuItemsExcluded")
          ?.value || []
      );

      setCategoryBrandPriorities(
        babbageAccounts
          ?.find((val) => val.field === "categoryBrandPriorities")
          ?.value?.map((priority) => {
            return {
              category: priority?.category?.["@id"],
              brand: priority?.brand?.["@id"],
              priority: priority?.priority,
            };
          }) || []
      );
      setCategoryBrandRestrictions(
        babbageAccounts
          ?.find((val) => val.field === "categoryBrandRestrictions")
          ?.value?.map((restriction) => {
            return {
              category: restriction?.category?.["@id"],
              brand: restriction?.brand?.["@id"],
            };
          }) || []
      );
    }
    setRestrictedBrands(
      babbageAccounts
        ?.find((val) => val.field === "categoryBrandRestrictions")
        ?.value?.filter(
          (restriction) =>
            restriction?.category?.["@id"] === selectedCategory?.value
        )
        ?.map((restriction) => {
          return {
            brand: restriction?.brand?.["@id"],
            category: restriction?.category?.["@id"],
            label: brands?.find(
              (brand) => restriction?.brand?.["@id"] === brand?.value
            )?.label,
            value: restriction?.brand?.["@id"],
          };
        }) || []
    );
    setPrioritisedBrands(
      babbageAccounts
        ?.find((val) => val.field === "categoryBrandPriorities")
        ?.value?.filter(
          (priority) => priority?.category?.["@id"] === selectedCategory?.value
        )
        ?.map((priority) => {
          return {
            brand: priority?.brand?.["@id"],
            category: priority?.category?.["@id"],
            priority: priority?.priority,
            id: crypto.randomUUID(),
          };
        }) || []
    );
    setThemeColours({
      primaryColour:
        babbageAccounts?.find(
          (val) => val.field === "customBabbageAccountTheme"
        )?.value?.primaryColour || "#0033A0",
      secondaryColour:
        babbageAccounts?.find(
          (val) => val.field === "customBabbageAccountTheme"
        )?.value?.secondaryColour || "#FDDA24",
      tertiaryColour:
        babbageAccounts?.find(
          (val) => val.field === "customBabbageAccountTheme"
        )?.value?.tertiaryColour || "#001E60",
    });
    // eslint-disable-next-line
  }, [babbageAccounts]);

  useEffect(() => {
    setSelectedCategory(null);
    setRestrictedBrands([]);
    setPrioritisedBrands([]);
  }, [mainAccountNumber]);

  const resetColours = () => {
    setThemeColours({
      primaryColour: "#0033A0",
      secondaryColour: "#FDDA24",
      tertiaryColour: "#001E60",
    });
  };

  useEffect(() => {
    const getT3CategoryOptions = async () => {
      setT3CategoriesLoading(true);
      try {
        const { data } = await client(
          GET_LIST,
          "get-active-plp-level-categories",
          {
            id: "get-active-plp-level-categories",
          }
        );

        const options = data.map((item) => {
          return {
            label: `${item?.heading} (${item?.originId}) - ${item?.parent?.parent?.parent?.heading} > ${item?.parent?.parent?.heading} > ${item?.parent?.heading}`,
            displayComponent: (
              <div className="p-2 hover:bg-blue-100 hover:cursor-pointer">
                {item?.heading} ({item?.originId})
                <span style={{ color: "#c8c8c8", fontWeight: 300 }}>
                  {" - "}
                  {item?.parent?.parent?.parent?.heading}
                  {" > "}
                  {item?.parent?.parent?.heading}
                  {" > "}
                  {item?.parent?.heading}
                </span>
              </div>
            ),
            value: item.id,
          };
        });

        setT3CategoryOptions(options);
      } catch (e) {
        console.error(e);

        toast.error(
          e?.error?.body?.hasOwnProperty("hydra:description")
            ? e.error.body["hydra:description"]
            : "There was an error submitting this form."
        );
      } finally {
        setT3CategoriesLoading(false);
      }
    };
    getT3CategoryOptions();
  }, [client, setT3CategoriesLoading]);

  useEffect(() => {
    const onFetchData = () =>
      client(GET_LIST, "brands", {
        page: 1,
        filter: {
          itemsPerPage: 10000,
        },
      });

    const getBrands = async () => {
      try {
        setBrandsLoading(true);
        const { data } = await onFetchData();
        const options = data?.map((item) => ({
          label: `${item?.name} (${item?.originId})`,
          value: item.id,
        }));

        setBrands(options);
      } catch (e) {
        console.error(e);

        toast.error(
          e?.message ? e.message : "There was an error submitting this form."
        );
      } finally {
        setBrandsLoading(false);
      }
    };
    getBrands();
  }, [client]);

  const Option = ({ data, innerProps }) => (
    <div {...innerProps}>{data.displayComponent}</div>
  );

  const SingleValue = ({ data }) => <div>{data.displayComponent}</div>;

  return (
    <>
      <Helmet>
        <title>Babbage Accounts | Peracto</title>
      </Helmet>
      <Heading name="Babbage Accounts" />

      <div className="row">
        <div className="col">
          <Form
            autoComplete="on"
            values={{
              mainAccountNumber: "",
            }}
            schema={schema}
            showActionPanel={false}
            onSubmit={onSubmitSearchForm}
          >
            <Group
              key="babbage-account-number"
              id="babbage-account-number"
              name="Babbage Account Number"
            >
              <Input
                name="mainAccountNumber"
                label="Main Account Number"
                testId="mainAccountNumber"
                required
              />

              <button
                className="btn btn-primary"
                type="submit"
                data-testid="submit"
              >
                Submit
              </button>
            </Group>
          </Form>

          {loading ||
          (babbageAccounts.length > 0 && categoriesLoading) ||
          (babbageAccounts.length > 0 && t3CategoriesLoading) ||
          (babbageAccounts.length > 0 && brandsLoading) ? (
            <div className="flex items-center justify-center py-20">
              <LoadingIcon />
            </div>
          ) : babbageAccounts.length > 0 ? (
            <Group
              key="babbage-account-settings"
              id="babbage-account-settings"
              name="Babbage Account Settings"
            >
              <Form
                autoComplete="on"
                values={{
                  mainAccountNumber: babbageAccounts?.find(
                    (val) => val.field === "mainAccountNumber"
                  )?.value,
                  restrictHaynesProAccess,
                  hideOmnihub,
                  excludeMenuItems,
                  primaryColour: themeColours?.primaryColour,
                  secondaryColour: themeColours?.secondaryColour,
                  tertiaryColour: themeColours?.tertiaryColour,
                  customLogo:
                    babbageAccounts?.find(
                      (val) => val.field === "customBabbageAccountTheme"
                    )?.value?.customLogo || "",
                  showStrippedFooter: showStrippedFooter,
                  isKeyAccount: isKeyAccount,
                  categoryBrandRestrictions: categoryBrandRestrictions,
                  categoryBrandPriorities: categoryBrandPriorities,
                }}
                showActionPanel={false}
                onSubmit={onUpdateAccount}
              >
                <p className="alert alert-info mt-[5px] font-light">
                  Updating settings for account:{" "}
                  <span className="font-bold">
                    {
                      babbageAccounts?.find(
                        (val) => val.field === "mainAccountNumber"
                      )?.value
                    }
                  </span>
                </p>
                <div className="mb-10">
                  <div className="mb-2">
                    <p className="mb-1 text-[13.6px] text-brand-grey-700">
                      Category Settings
                    </p>
                    <p className="text-[12.8px] text-[#aaaaaa] mb-2 font-[300]">
                      Select a category to hide or sort brands
                    </p>

                    <Select
                      options={t3CategoryOptions}
                      name="t3Categories"
                      testId="t3_categories"
                      classNamePrefix="select"
                      placeholder="Select a category"
                      value={selectedCategory}
                      components={{ Option, SingleValue }}
                      isSearchable
                      className="basic-single"
                      onChange={(e) => {
                        setRestrictedBrands([]);
                        setPrioritisedBrands([]);
                        setSelectedCategory(e || null);
                        const selectedCategoryId = e?.value;

                        const filteredPriorities =
                          categoryBrandPriorities?.filter(
                            (priority) =>
                              priority.category === selectedCategoryId
                          );

                        setRestrictedBrands(
                          categoryBrandRestrictions
                            ?.filter(
                              (restriction) =>
                                restriction.category === selectedCategoryId
                            )
                            ?.map((restriction) => {
                              return {
                                ...restriction,
                                label: brands?.find(
                                  (brand) => restriction?.brand === brand?.value
                                )?.label,
                                category: selectedCategoryId,
                                value: restriction?.brand,
                              };
                            })
                        );
                        setPrioritisedBrands(
                          filteredPriorities
                            ?.map((priority) => {
                              return { ...priority, id: crypto.randomUUID() };
                            })
                            .sort(
                              (a, b) => (a?.priority ?? 0) - (b?.priority ?? 0)
                            )
                        );
                      }}
                      isLoading={t3CategoriesLoading}
                      isDisabled={t3CategoriesLoading}
                    />
                  </div>

                  <BrandRestrictor
                    brands={brands}
                    restrictedBrands={restrictedBrands}
                    setRestrictedBrands={setRestrictedBrands}
                    brandsLoading={brandsLoading}
                    selectedCategory={selectedCategory}
                    categoryBrandRestrictions={categoryBrandRestrictions}
                    setCategoryBrandRestrictions={setCategoryBrandRestrictions}
                  />
                  <BrandSorter
                    brandOptions={brands}
                    brandsLoading={brandsLoading}
                    selectedCategory={selectedCategory}
                    categoryBrandPriorities={categoryBrandPriorities}
                    setCategoryBrandPriorities={setCategoryBrandPriorities}
                    prioritisedBrands={prioritisedBrands}
                    setPrioritisedBrands={setPrioritisedBrands}
                  />
                </div>
                <Checkbox
                  name="restrictHaynesProAccess"
                  label="Restrict Haynes Pro Access?"
                  testId="restrict_haynes_pro_access"
                  value={restrictHaynesProAccess}
                  onChange={(e) => setRestrictHaynesProAccess(e.target.checked)}
                />
                <Checkbox
                  name="hideOmnihub"
                  label="Hide Omnihub?"
                  testId="hide_omnihub"
                  value={hideOmnihub}
                  onChange={(e) => setHideOmnihub(e.target.checked)}
                />

                <div className="mb-4">
                  <label
                    className="block mb-1 font-medium"
                    htmlFor="excludeMenuItems"
                  >
                    Excluded Menu Items
                  </label>
                  <Select
                    id="excludeMenuItems"
                    name="excludeMenuItems"
                    isMulti
                    options={categoryOptions}
                    value={excludeMenuItems}
                    onChange={setExcludeMenuItems}
                    classNamePrefix="react-select"
                    placeholder="Select menu items to exclude..."
                    isDisabled={categoriesLoading}
                  />
                  <small className="text-gray-500">
                    Prevents the selected menu items from displaying for the
                    Babbage account
                  </small>
                </div>

                <Checkbox
                  name="isKeyAccount"
                  label="Allow colour and logo customisation?"
                  testId="is_key_account"
                  value={isKeyAccount}
                  onChange={(e) => {
                    setIsKeyAccount(e.target.checked);
                    if (!e.target.checked) {
                      setShowStrippedFooter(false);
                    }
                  }}
                />
                <div style={{ opacity: isKeyAccount ? 1 : 0.5 }}>
                  <Checkbox
                    name="showStrippedFooter"
                    label="Display stripped-back Omnipart footer?"
                    testId="show_stripped_footer"
                    help="Removes the LKQ logo, Euro Car Parts logo and award images from the footer"
                    disabled={!isKeyAccount}
                    value={showStrippedFooter}
                    onChange={(e) => setShowStrippedFooter(e.target.checked)}
                  />
                </div>

                {/* File input with opacity wrapper */}
                <div className={`${!isKeyAccount ? "opacity-50" : ""}`}>
                  <File
                    name="customLogo"
                    label="Custom Logo"
                    disabled={!isKeyAccount}
                    help="Custom logo for the site header. Recommended: width: 200px, height: 60px, file size up to 50kb. Accepted formats: .png, .jpg, .jpeg, .svg"
                  />
                </div>

                {babbageAccounts?.find((val) => val.field === "customLogo")
                  ?.value && (
                  <div className="mt-2 mb-4">
                    <img
                      src={
                        babbageAccounts.find(
                          (val) => val.field === "customLogo"
                        ).value
                      }
                      alt="Custom Logo Preview"
                      className="max-h-[100px] object-contain"
                    />
                  </div>
                )}

                {/* Color inputs with Tailwind opacity classes */}
                <div
                  className={`w-full md:w-1/3 ${
                    !isKeyAccount ? "opacity-50" : ""
                  }`}
                >
                  <Input
                    name="primaryColour"
                    type="color"
                    label="Primary Theme Colour"
                    help="Headings, coloured text and icons, background areas and buttons"
                    disabled={!isKeyAccount}
                    value={themeColours?.primaryColour ?? ""}
                    onChange={(e) =>
                      setThemeColours({
                        ...themeColours,
                        primaryColour: e.target.value,
                      })
                    }
                  />
                </div>

                <div
                  className={`w-full md:w-1/3 ${
                    !isKeyAccount ? "opacity-50" : ""
                  }`}
                >
                  <Input
                    name="secondaryColour"
                    type="color"
                    label="Secondary Theme Colour"
                    help="Success buttons and accent colours"
                    disabled={!isKeyAccount}
                    value={themeColours?.secondaryColour ?? ""}
                    onChange={(e) =>
                      setThemeColours({
                        ...themeColours,
                        secondaryColour: e.target.value,
                      })
                    }
                  />
                </div>

                <div
                  className={`w-full md:w-1/3 ${
                    !isKeyAccount ? "opacity-50" : ""
                  }`}
                >
                  <Input
                    name="tertiaryColour"
                    type="color"
                    label="Tertiary Theme Colour"
                    help="Utility bar and accent colours"
                    disabled={!isKeyAccount}
                    value={themeColours?.tertiaryColour ?? ""}
                    onChange={(e) =>
                      setThemeColours({
                        ...themeColours,
                        tertiaryColour: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="flex flex-col w-fit gap-y-8">
                  <div>
                    <button
                      className="btn btn-primary "
                      data-testid="reset-colours"
                      onClick={resetColours}
                      type="button"
                      disabled={!isKeyAccount}
                    >
                      Reset Colours to Default
                    </button>
                    <p
                      className={`mt-2 text-[12.8px] font-[300] text-[#AAAAAA] ${
                        !isKeyAccount ? "opacity-50" : ""
                      }`}
                    >
                      This will reset the colours to the default Omnipart
                      colours.
                    </p>
                  </div>

                  <button
                    className="btn btn-success w-fit"
                    type="submit"
                    data-testid="submit"
                  >
                    Save
                  </button>
                </div>
              </Form>
            </Group>
          ) : noResults ? (
            <div className="py-20 text-center">
              No Babbage Accounts found for "{mainAccountNumber}"
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};
